/**
 * File-transfer resume state — sender-side schema (M2).
 * docs/file-transfer-resume.md §2.1/§2.2 (record shape), §11 (constants).
 *
 * Deliberately does NOT import `@reflecto/core`, for a LOCAL reason, not a package rule:
 * this is persisted resume state, and the manifest travels as serialized JSON
 * (`manifestJson`) rather than a nested typed `TransferManifest` so the stored shape has
 * no compile-time dependency on the wire type's evolution; the app layer parses +
 * validates it at load time (§2.4). (An earlier version of this comment cited
 * `api/devices.ts` — deleted in slice 11d — as the model of a package-wide "does not
 * import core" convention. No such convention exists: 18 files in this package import
 * `@reflecto/core`. The real boundary is ownership, stated in `packages/client-core/CLAUDE.md`:
 * core OWNS the crypto primitives and wire types; this package never redefines them.)
 *
 * `manifestJson` is E2E-local content (contains the filename): never sent
 * to any server API, never logged (security.md).
 *
 * The receiver counterpart (`FtReceiverStateRecord`) lives in this same
 * module (added by TASK-15).
 */

import type { FtTransferStatus } from "./lifecycle";

/**
 * Persisted transfer lifecycle status. Defined in `lifecycle.ts` (the single
 * home for the status vocabulary and its terminal/active partitions) and
 * re-exported here, where the records that carry it live.
 */
export type { FtTransferStatus };

/**
 * Sender-side resume record (resume spec §2.2). One record per outbound
 * transfer, keyed by `transferId` in `ReflectoStorage.ftSenderStates`
 * (extension/PWA) or the Room `sender_transfer_state` table (Android).
 *
 * Field-for-field identical (camelCase) across TS and Kotlin — Room column
 * names equal these field names (resume spec §2.1).
 */
export interface FtSenderStateRecord {
  /** Record-schema version. Drop the record (and its artifacts) on load if `v !== 1` (§2.4). */
  v: 1;
  transferId: string;
  /** recipientDeviceIds[0] — ctrl target + FtApi counterparty. */
  peerDeviceId: string;
  /**
   * Base64 recipient X25519 PUBLIC key, persisted (not re-read from current
   * pairing state) on purpose: already-uploaded ciphertext is bound to the
   * key pair in effect when the transfer started (§2.5).
   */
  peerPub: string;
  /** Serialized `TransferManifest`. `""` allowed on terminal tombstones (§8.5 receiver mirror; sender records don't currently write empty tombstones but the type allows it defensively). */
  manifestJson: string;
  /** `ceil(chunkCount / CHUNKS_PER_PART)`; equals the value sent to `POST /transfers`. */
  partCount: number;
  /** Ascending. Local cache of the server SET — the server wins on any disagreement (§5.2 step 3). */
  partsUploaded: number[];
  /** Parts `< this` are never uploaded (from `accept.resumeFromChunk` / `CHUNKS_PER_PART`, §8.4). 0 default. */
  skipBelowPart: number;
  /** Never `"declined"` on sender records. */
  status: FtTransferStatus;
  /** FtErrorCode; set iff `status` is `"failed" | "expired"`. */
  errorCode?: string;
  /** Unix ms from `POST /transfers` 201 / `GET /transfers/:id` (advisory — §7.3). */
  expiresAt: number;
  createdAt: number;
  /** Bumped on every write. */
  updatedAt: number;
  /** EXTENSION/PWA ONLY: OPFS source copy path, `"ft/send-<transferId>"` (§3.1.4). */
  opfsPath?: string;
}

/**
 * Receiver-side resume record (resume spec §2.3). One record per inbound
 * transfer, keyed by `transferId` in `ReflectoStorage.ftReceiverStates`
 * (extension/PWA) or the Room `receiver_transfer_state` table (Android).
 *
 * Field-for-field identical (camelCase) across TS and Kotlin — Room column
 * names equal these field names (resume spec §2.1). Deliberately carries NO
 * `resumeFromChunk` field: the receiver's own `chunksVerified` IS the value
 * it emits as `resumeFromChunk` (§8.4), and its download loop always starts
 * at `ceil(chunksVerified / CHUNKS_PER_PART)` (§6.2 step 7).
 */
export interface FtReceiverStateRecord {
  /** Record-schema version. Drop the record (and its artifacts) on load if `v !== 1` (§2.4). */
  v: 1;
  transferId: string;
  /** The offer's cryptographic sender (the paired phone in M1/M2). */
  peerDeviceId: string;
  /**
   * Base64 SENDER X25519 PUBLIC key, persisted on purpose (not re-read from
   * current pairing) — already-received ciphertext is bound to the key pair
   * in effect when the transfer started (§2.5).
   */
  peerPub: string;
  /** Serialized `TransferManifest`. `""` allowed on `"declined"`/`"cancelled"` tombstones written pre-accept (§8.3). */
  manifestJson: string;
  /** `ceil(chunkCount/80)`; computed at accept, cross-checked vs the server GET (§6.2 step 2). */
  partCount: number;
  /**
   * Parts fully decrypted AND written+flushed. M2 invariant:
   * `== [0 .. ceil(chunksVerified/80) - 1]` — `ceil` because the final part
   * may cover < 80 chunks (kept as a set for M3 headroom).
   */
  partsDownloaded: number[];
  /**
   * High-water mark: count of chunks durably written per the record (always
   * a multiple of `CHUNKS_PER_PART`, or `== chunkCount` at the end).
   */
  chunksVerified: number;
  status: FtTransferStatus;
  /** FtErrorCode; set iff `status` is `"failed" | "expired"`. */
  errorCode?: string;
  /** Unix ms when the accept ctrl was first sent — drives CREATE_WAIT (§6.3). */
  acceptedAt: number;
  /** `0` until the first successful `GET /transfers/:id`, then the server value (§9.2). */
  expiresAt: number;
  createdAt: number;
  /** Bumped on every write. */
  updatedAt: number;
  /** EXTENSION/PWA ONLY: OPFS output path, `"ft/recv-<transferId>"` (§3.1.4). */
  opfsPath?: string;
}

// --- Constants (§11) — normative values, TS side. Kotlin mirror lives in
// `TransferStateConstants` beside `Protocol.kt`. The CLI (M4) redeclares
// these locally (§3.3). Wire/crypto constants (CHUNK_SIZE, CHUNKS_PER_PART,
// …) stay in `@reflecto/core` and are NOT duplicated here. `PART_RETRY_BACKOFF_MS`
// and `ACCEPT_TIMEOUT_MS` already exist in `sendFile.ts` and are not
// redeclared here either (per §11's table).

/** Record-schema version; §2.4 drop rule. */
export const FT_STATE_VERSION = 1;

/** Receiver 404 grace period after sending `accept`, before EXPIRED (§6.3). */
export const FT_CREATE_WAIT_MS = 60_000;

/** Availability polling interval (§6.2/§6.3). */
export const FT_POLL_ACTIVE_MS = 2_000;

/** No-progress threshold before dropping to the idle poll interval (§6.2 step 7). */
export const FT_POLL_IDLE_AFTER_MS = 300_000;

/**
 * Post-accept liveness deadline (#821): give up waiting for a part after this long with NO new part
 * appearing, rather than polling until the server's 12h `FT_TTL_SECONDS` expiry.
 *
 * Without it, a sender that dies silently AFTER creating the transfer (the #823 signature — the
 * server keeps answering `GET /transfers/:id` with 200 and an unchanging `partsUploaded`) leaves the
 * receiver polling for TWELVE HOURS while holding its OPFS sync-access handle, pinning the
 * extension's offscreen document open (the idle-close scheduler will not close a document with an
 * "active" transfer) and showing "receiving" in the UI the whole time.
 *
 * Deliberately NOT terminal: the timeout raises the existing stop signal, which leaves the record
 * `"transferring"` so the ordinary resume scan retries it later and a re-offer still heals it
 * (`fileCtrl.ts`'s "transferring" → re-accept branch). That keeps a merely-slow sender — an Android
 * uploader suspended by Doze, say — recoverable instead of being killed, while still releasing the
 * resources a zombie receive was pinning.
 *
 * 3x [FT_POLL_IDLE_AFTER_MS], the point at which this code already treats silence as normal and
 * drops to the idle cadence. A ~5MiB part takes ~50s at 100KB/s and ~8min even at 10KB/s, so 15min
 * of ZERO new parts means the sender is gone, not slow.
 */
export const FT_RECEIVE_LIVENESS_MS = 900_000;

/** Idle poll interval (§6.2 step 7). */
export const FT_POLL_IDLE_MS = 60_000;

/** Accept re-send cadence inside CREATE_WAIT (§6.3). */
export const FT_ACCEPT_RESEND_MS = 30_000;

/** Tombstone retention — 7 days (§8.5). */
export const FT_STATE_RETENTION_MS = 604_800_000;

/** Max terminal records retained per direction (§8.5). */
export const FT_STATE_MAX_RECORDS = 50;

/** OPFS quota preflight margin — 64 MiB (§3.1.4). */
export const FT_OPFS_QUOTA_MARGIN_BYTES = 67_108_864;

/** Offer TTL — 1 h (D6; equals `ftOfferStore.ts`'s `OFFER_EXPIRY_MS`, §7.1). */
export const FT_OFFER_TTL_MS = 3_600_000;
